import { useEffect, useState } from "react";
import { Link } from "easy-page-router/react";
import { useSSRHook } from "ssr-hook";
import Icon from "@mdi/react";
import { mdiPlus } from "@mdi/js";

import { useHead } from "../../services/common";
import { Footer, Header } from "../../Layout";
import { Loading, Page, ReloadLine, ShareButton } from "../../components";
import { useLang } from "../../services/lang";
import { api, handleError, useHandleError } from "../../services/api";
import { getLocalId } from "easy-analytics/client";
import { useLocalHistory } from "../../services/localStorage";

import { PollTable } from "./components/PollTable";
import { PollVoting } from "./components/PollVoting";
import { PollClient, PollVotes } from "../../../types";

export default function PollPage({ id }: { id: string }) {
    const { dict } = useLang();

    useLocalHistory("poll", id);

    const [poll, error, isLoading, reload] = useSSRHook<PollClient>(`/api/poll?pollId=${encodeURIComponent(id)}`);
    useHandleError(error);

    const [isVoteMode, setIsVoteMode] = useState(false);
    const [vote, setVote] = useState<null | PollVotes>(null);

    const [message, setMessage] = useState("");

    useEffect(() => {
        if (vote !== null) return;

        api.getPollVote({ pollId: id, userId: getLocalId() }).then(([vote, error]) => {
            if (error) {
                handleError(error);
                setVote({});
                return;
            } else {
                setVote(vote);
            }
        });
    }, [id, vote]);

    const userVoteCount = Object.values(vote || {}).filter(v => v === true).length;

    const title = dict.seo.poll.getTitle(poll?.title);
    const description = dict.seo.poll.getDescription(poll?.title, poll?.description);

    useHead({
        title,
        description,
    });

    function handleChangeVote(message: string) {
        setMessage(message);
        setIsVoteMode(false);
        reload();
        setVote(null);
    }

    return <Page theme={poll?.theme} fixed={<>
        <Header title={dict.what} link={`/${dict.urlPoll}`}>
            <Link href={`/${dict.urlPoll}/${dict.urlNew}`}><Icon path={mdiPlus} size={1} />{dict.newPoll}</Link>
            <ShareButton title={title} description={description} />
        </Header>
        <Footer />
        <ReloadLine stopReload={isVoteMode} value={poll} error={error} isLoading={isLoading} onReload={reload} />
    </>}>
        <div className={`full-page poll`}>

            {isLoading && <Loading />}
            {error && <p className="error">{dict.pollsError}</p>}
            {poll && <>
                <h1>{poll.title}</h1>
                {poll.description && <p className="description width-max pre-line">{poll.description}</p>}

                {!isVoteMode && <>
                    <button className="center" onClick={() => setIsVoteMode(true)}>
                        {userVoteCount === 0 ? dict.pollVote : dict.pollChangeVote}
                    </button>
                    {message && <p className="info">{message}</p>}
                    <PollTable poll={poll} />
                </>}

                {isVoteMode && vote === null && <Loading />}
                {isVoteMode && vote && <PollVoting poll={poll} vote={vote} onChange={handleChangeVote} />}
            </>}
        </div>
    </Page>;
}
